import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Plus } from 'lucide-react';
import { useEvent, useApiErrors, useLazyCommand } from '@soap/modules';
import { tenantCustomCatalogSubjectRowsFromLibrary } from '../utils/curriculumUtils';
import {
  parseEducationSystemDefinitionsEvent,
  getDefinitionForKey,
  getYearsForKeyStageId,
  isPlacementIdsValidForDefinition,
  type EducationSystemDefinition
} from '../../../utils/educationSystemDefinitions';
import { SelectOrCreateDropdown } from './SelectOrCreateDropdown';
import {
  buildC314BandsAddingKeyStageOnly,
  buildC314BandsAddingYearOnly,
  buildC314BandsEnsuringPlacement,
  isTenantCustomEducationSystemKey,
  tenantCustomSystemIdFromKey
} from '../../../utils/customEducationSystemPlacement';
import { AppMessageDialog, PLACEMENT_APP_MSG_TITLE } from '../../AppMessageDialog';

export interface AddCustomTopicDialogProps {
  showDialog: boolean;
  onClose: () => void;
  tenantCustomCurriculumLibraryData?: any | null;
  /** Pre-selected subject from Create Your Own wizard (step 2 → single topic). */
  initialCatalogSubjectId?: string;
  /** Hide subject dropdown and "add subject" links when subject was chosen in the wizard. */
  hideSubjectPicker?: boolean;
  onLoadFormData: () => void;
  onSubmit: (command: Record<string, unknown>) => void;
  onLoadEducationSystemDefinitions?: () => void;
  onRefreshTenantCustomCurriculum?: () => void;
  /** Close this dialog and open Custom Subject (`C316v1_EnsureCustomSubjectInTenantCustomCurriculum`). */
  onOpenCustomSubjectShell?: () => void;
}

function collectTopicsInYearBucket(topics: any[] | undefined, out: { id: string; name: string }[]): void {
  if (!topics?.length) return;
  for (const t of topics) {
    if (t?.e179_TopicId) {
      out.push({ id: String(t.e179_TopicId), name: String(t.e179_Name ?? '').trim() || '(Untitled)' });
    }
    if (t?.e179_ChildTopics?.length) {
      collectTopicsInYearBucket(t.e179_ChildTopics, out);
    }
  }
}

function getCatalogTopicsForPlacement(
  catalog: any,
  catalogSubjectId: string,
  keyStageId: string,
  educationSystemYearId: string
): { id: string; name: string }[] {
  const subjects = catalog?.e179_Subjects ?? [];
  const wantY = (educationSystemYearId ?? '').trim();
  const wantKs = (keyStageId ?? '').trim();
  const wantSubj = (catalogSubjectId ?? '').trim();
  if (!wantY || !wantKs || !wantSubj) return [];
  const subject = subjects.find((s: any) => String(s.e179_SubjectId ?? '').trim() === wantSubj);
  if (!subject) return [];
  const keyStages = subject.e179_KeyStages ?? [];
  const ks = keyStages.find((k: any) => String(k.e179_KeyStageId ?? '').trim() === wantKs);
  if (!ks) return [];
  const years = ks.e179_Years ?? [];
  for (const year of years) {
    const idRaw = year.e179_YearId ?? year.E179_YearId;
    if (idRaw != null && String(idRaw).trim() === wantY) {
      const out: { id: string; name: string }[] = [];
      collectTopicsInYearBucket(year.e179_Topics, out);
      return out;
    }
  }
  return [];
}

function readFieldString(fieldData: any[], name: string): string {
  const v = fieldData.find((f: any) => f.name === name)?.initialValue;
  if (v == null || v === '') return '';
  return String(v);
}

function readFieldOptionalNumber(fieldData: any[], name: string): string {
  const v = fieldData.find((f: any) => f.name === name)?.initialValue;
  if (v == null || v === '') return '';
  return String(v);
}

function pipeStringToList(s: string): string[] {
  if (!s.trim()) return [];
  return s
    .split('|')
    .map((x) => x.trim())
    .filter(Boolean);
}

function parseKeywordsFromPipe(s: string): Array<{ keyword: string; description: string }> {
  if (!s.trim()) return [];
  return s
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const idx = part.indexOf(':');
      if (idx === -1) return { keyword: part, description: '' };
      return {
        keyword: part.slice(0, idx).trim(),
        description: part.slice(idx + 1).trim()
      };
    })
    .filter((k) => k.keyword.length > 0);
}

/** Tenant custom "extend placement" UI only: labels from create/select controls → E313 ids. */
function extendPlacementIdsFromBandLabels(
  def: EducationSystemDefinition | undefined,
  keyStageName: string,
  yearName: string
): { keyStageId: string; yearId: string } | null {
  if (!def?.keyStages?.length) return null;
  const wantKs = keyStageName.trim().toLowerCase();
  const wantY = yearName.trim().toLowerCase();
  if (!wantKs || !wantY) return null;
  const ks = def.keyStages.find((k) => k.name.trim().toLowerCase() === wantKs);
  const keyStageId = ks?.keyStageId?.trim();
  if (!ks || !keyStageId) return null;
  const hit = ks.years.find((y) => y.yearName.trim().toLowerCase() === wantY);
  if (!hit) return null;
  return { keyStageId, yearId: hit.yearId };
}

export function AddCustomTopicDialog({
  showDialog,
  onClose,
  tenantCustomCurriculumLibraryData,
  initialCatalogSubjectId,
  hideSubjectPicker = false,
  onLoadFormData,
  onSubmit,
  onLoadEducationSystemDefinitions,
  onRefreshTenantCustomCurriculum,
  onOpenCustomSubjectShell
}: AddCustomTopicDialogProps) {
  const customCurriculumTitle = 'Custom curriculum';

  const [sendSaveEducationStructure] = useLazyCommand('C314v1_SaveTenantEducationSystemStructure');

  const [formReady, setFormReady] = useState(false);

  const [selectedCatalogSubjectId, setSelectedCatalogSubjectId] = useState('');
  const [placementDefs, setPlacementDefs] = useState<EducationSystemDefinition[]>([]);
  const [placementKeyStageName, setPlacementKeyStageName] = useState('');
  const [placementYearLabel, setPlacementYearLabel] = useState('');
  /** Built-in / fixed E313 bands: stable ids only (no name resolution). */
  const [placementKeyStageId, setPlacementKeyStageId] = useState('');
  const [placementYearId, setPlacementYearId] = useState('');

  const [topicName, setTopicName] = useState('');
  const [topicDetails, setTopicDetails] = useState('');
  const [minHours, setMinHours] = useState('');
  const [priorKnowledgeList, setPriorKnowledgeList] = useState<string[]>([]);
  const [nationalCurriculumList, setNationalCurriculumList] = useState<string[]>([]);
  const [keywordRows, setKeywordRows] = useState<Array<{ keyword: string; description: string }>>([]);
  const [learningObjectivesList, setLearningObjectivesList] = useState<string[]>([]);
  const [supervisionLevel, setSupervisionLevel] = useState('');
  const [outcome, setOutcome] = useState('');
  const [teacherTip, setTeacherTip] = useState('');
  const [misconceptions, setMisconceptions] = useState('');
  const [precedingCatalogTopicId, setPrecedingCatalogTopicId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [placementStructureSaving, setPlacementStructureSaving] = useState(false);
  const [appMessage, setAppMessage] = useState<{ title: string; message: string } | null>(null);

  const showAppMessage = (message: string, title: string) => {
    setAppMessage({ title, message });
  };

  /** Avoid resetting to keyStages[0] when E313 is refreshed (e.g. after adding a key stage). */
  const lastPlacementEducationKeyRef = useRef<string | undefined>(undefined);

  useApiErrors(() => {
    setSubmitting(false);
    setPlacementStructureSaving(false);
  });

  const subjectRows = useMemo(
    () => tenantCustomCatalogSubjectRowsFromLibrary(tenantCustomCurriculumLibraryData),
    [tenantCustomCurriculumLibraryData]
  );

  const catalogReady = tenantCustomCurriculumLibraryData != null;

  const selectedSubjectRow = useMemo(
    () => subjectRows.find((r) => r.catalogSubjectId === selectedCatalogSubjectId),
    [subjectRows, selectedCatalogSubjectId]
  );

  const subjectName = selectedSubjectRow?.name.trim() ?? '';
  const resolvedEducationSystemKey = selectedSubjectRow?.educationSystemKey.trim() ?? '';

  const currentPlacement = useMemo(
    () => getDefinitionForKey(placementDefs, resolvedEducationSystemKey),
    [placementDefs, resolvedEducationSystemKey]
  );

  const allowExtendPlacement = useMemo(
    () =>
      Boolean(resolvedEducationSystemKey) && isTenantCustomEducationSystemKey(resolvedEducationSystemKey),
    [resolvedEducationSystemKey]
  );

  const yearValue = placementYearLabel.trim();
  const placementReady =
    Boolean(currentPlacement) && (allowExtendPlacement || Boolean(currentPlacement?.keyStages?.length));
  const yearValid = allowExtendPlacement ? yearValue.length > 0 : placementYearId.trim().length > 0;

  const existingKeyStageNames = useMemo(
    () =>
      (currentPlacement?.keyStages ?? [])
        .map((ks) => ks.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [currentPlacement]
  );

  useEvent({
    eventName: 'E183v1_GotAddCustomTopicFormData',
    onEventReceived: (event: any) => {
      const fieldData = event.e000_FieldData || [];

      setTopicName(readFieldString(fieldData, 'c262_TopicName'));
      setTopicDetails(readFieldString(fieldData, 'c262_TopicDetails'));
      setMinHours(readFieldOptionalNumber(fieldData, 'c262_MinHours'));
      setPriorKnowledgeList(pipeStringToList(readFieldString(fieldData, 'c262_PriorKnowledgeRequired')));
      setNationalCurriculumList(pipeStringToList(readFieldString(fieldData, 'c262_NationalCurriculumContent')));
      setKeywordRows(parseKeywordsFromPipe(readFieldString(fieldData, 'c262_Keywords')));
      setLearningObjectivesList(pipeStringToList(readFieldString(fieldData, 'c262_LearningObjectives')));
      setSupervisionLevel(readFieldString(fieldData, 'c262_SupervisionLevel'));
      setOutcome(readFieldString(fieldData, 'c262_Outcome'));
      setTeacherTip(readFieldString(fieldData, 'c262_TeacherTip'));
      setMisconceptions(readFieldString(fieldData, 'c262_Misconceptions'));
      setPrecedingCatalogTopicId(readFieldString(fieldData, 'c262_PrecedingCatalogTopicId'));

      setFormReady(true);
    }
  });

  useEvent({
    eventName: 'E313v1_GotEducationSystemDefinitions',
    onEventReceived: (event: any) => {
      setPlacementDefs(parseEducationSystemDefinitionsEvent(event));
    }
  });

  useEvent({
    eventName: 'E181v1_TopicsAddedToStudentCurriculum',
    onEventReceived: () => {
      setSubmitting(false);
    }
  });

  useEffect(() => {
    if (!showDialog) return;
    setFormReady(false);
    setSelectedCatalogSubjectId((initialCatalogSubjectId ?? '').trim());
    onRefreshTenantCustomCurriculum?.();
    onLoadFormData();
    onLoadEducationSystemDefinitions?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open-only load; parent handlers may change identity
  }, [showDialog, initialCatalogSubjectId]);

  useEffect(() => {
    if (resolvedEducationSystemKey === lastPlacementEducationKeyRef.current) {
      return;
    }
    lastPlacementEducationKeyRef.current = resolvedEducationSystemKey;
    setPlacementKeyStageName('');
    setPlacementYearLabel('');
    setPlacementKeyStageId('');
    setPlacementYearId('');
  }, [resolvedEducationSystemKey]);

  useEffect(() => {
    if (!resolvedEducationSystemKey || placementKeyStageName) return;
    if (!allowExtendPlacement) return;
    const def = getDefinitionForKey(placementDefs, resolvedEducationSystemKey);
    const firstKs = def?.keyStages?.[0];
    if (!firstKs?.name) {
      setPlacementKeyStageName('');
      setPlacementYearLabel('');
      return;
    }
    setPlacementKeyStageName(firstKs.name);
    setPlacementYearLabel(firstKs.years?.[0]?.yearName ?? '');
  }, [resolvedEducationSystemKey, placementDefs, placementKeyStageName, allowExtendPlacement]);

  useEffect(() => {
    if (allowExtendPlacement || !resolvedEducationSystemKey) return;
    const def = getDefinitionForKey(placementDefs, resolvedEducationSystemKey);
    const withIds = (def?.keyStages ?? []).filter((k) => (k.keyStageId ?? '').trim() !== '');
    const first = withIds[0];
    const y0 = first?.years?.[0]?.yearId ?? '';
    setPlacementKeyStageId(first?.keyStageId ?? '');
    setPlacementYearId(y0);
  }, [allowExtendPlacement, resolvedEducationSystemKey, placementDefs]);

  const handleKeyStagePlacementChange = (name: string): void | Promise<void> => {
    const trimmed = name.trim();
    if (!allowExtendPlacement) {
      setPlacementKeyStageName(trimmed);
      return;
    }
    if (!trimmed) return;

    const def = getDefinitionForKey(placementDefs, resolvedEducationSystemKey);
    if (!def) {
      showAppMessage(
        'Placement is still loading. Wait a moment and try again.',
        PLACEMENT_APP_MSG_TITLE.notReady
      );
      throw new Error('Placement not ready');
    }
    const exists = def.keyStages.some(
      (k) => k.name.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      setPlacementYearLabel('');
      setPlacementKeyStageName(trimmed);
      return;
    }

    return (async () => {
      const systemId = tenantCustomSystemIdFromKey(resolvedEducationSystemKey);
      if (!systemId) {
        showAppMessage('Invalid custom education system key.', PLACEMENT_APP_MSG_TITLE.invalidSystem);
        throw new Error('Invalid system key');
      }
      const built = buildC314BandsAddingKeyStageOnly(def, trimmed, () => crypto.randomUUID());
      if (!built.ok) {
        showAppMessage(built.message, PLACEMENT_APP_MSG_TITLE.cannotSave);
        throw new Error(built.message);
      }
      if (!built.needsSave) {
        setPlacementYearLabel('');
        setPlacementKeyStageName(trimmed);
        return;
      }
      setPlacementStructureSaving(true);
      try {
        await sendSaveEducationStructure({
          C314_SystemId: systemId,
          C314_KeyStageBands: built.bands
        });
        setPlacementYearLabel('');
        setPlacementKeyStageName(trimmed);
        onLoadEducationSystemDefinitions?.();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : 'Could not save education system.';
        showAppMessage(msg, PLACEMENT_APP_MSG_TITLE.couldNotSave);
        throw e instanceof Error ? e : new Error(String(msg));
      } finally {
        setPlacementStructureSaving(false);
      }
    })();
  };

  const handleYearPlacementChange = (label: string): void | Promise<void> => {
    const yTrim = label.trim();
    if (!allowExtendPlacement) {
      setPlacementYearLabel(yTrim);
      return;
    }
    if (!placementKeyStageName.trim()) return;

    const def = getDefinitionForKey(placementDefs, resolvedEducationSystemKey);
    if (!def) {
      showAppMessage(
        'Placement is still loading. Wait a moment and try again.',
        PLACEMENT_APP_MSG_TITLE.notReady
      );
      throw new Error('Placement not ready');
    }
    const ks = def.keyStages.find(
      (k) => k.name.trim().toLowerCase() === placementKeyStageName.trim().toLowerCase()
    );
    const existing = (ks?.years ?? []).some((yl) => yl.yearName.toLowerCase() === yTrim.toLowerCase());
    if (existing || !yTrim) {
      setPlacementYearLabel(yTrim);
      return;
    }

    return (async () => {
      const systemId = tenantCustomSystemIdFromKey(resolvedEducationSystemKey);
      if (!systemId) {
        showAppMessage('Invalid custom education system key.', PLACEMENT_APP_MSG_TITLE.invalidSystem);
        throw new Error('Invalid system key');
      }
      const built = buildC314BandsAddingYearOnly(def, placementKeyStageName, yTrim, () => crypto.randomUUID());
      if (!built.ok) {
        showAppMessage(built.message, PLACEMENT_APP_MSG_TITLE.cannotSave);
        throw new Error(built.message);
      }
      if (!built.needsSave) {
        setPlacementYearLabel(yTrim);
        return;
      }
      setPlacementStructureSaving(true);
      try {
        await sendSaveEducationStructure({
          C314_SystemId: systemId,
          C314_KeyStageBands: built.bands
        });
        setPlacementYearLabel(yTrim);
        onLoadEducationSystemDefinitions?.();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : 'Could not save education system.';
        showAppMessage(msg, PLACEMENT_APP_MSG_TITLE.couldNotSave);
        throw e instanceof Error ? e : new Error(String(msg));
      } finally {
        setPlacementStructureSaving(false);
      }
    })();
  };

  useEffect(() => {
    if (!showDialog) {
      setAppMessage(null);
      lastPlacementEducationKeyRef.current = undefined;
      setFormReady(false);
      lastPlacementEducationKeyRef.current = undefined;
      setSelectedCatalogSubjectId('');
      setPlacementDefs([]);
      setPlacementKeyStageName('');
      setPlacementYearLabel('');
      setPlacementKeyStageId('');
      setPlacementYearId('');
      setTopicName('');
      setTopicDetails('');
      setMinHours('');
      setPriorKnowledgeList([]);
      setNationalCurriculumList([]);
      setKeywordRows([]);
      setLearningObjectivesList([]);
      setSupervisionLevel('');
      setOutcome('');
      setTeacherTip('');
      setMisconceptions('');
      setPrecedingCatalogTopicId('');
      setSubmitting(false);
      setPlacementStructureSaving(false);
    }
  }, [showDialog]);

  const catalogSubjectIdForSubmit = selectedSubjectRow ? selectedCatalogSubjectId.trim() : '';

  /** Built-in: ids from selects. Tenant extend: map band labels to E313 ids (or null until structure exists). */
  const placementFromE313 = useMemo(() => {
    if (allowExtendPlacement) {
      return extendPlacementIdsFromBandLabels(currentPlacement, placementKeyStageName, yearValue);
    }
    const ks = placementKeyStageId.trim();
    const y = placementYearId.trim();
    if (!ks || !y || !currentPlacement) return null;
    return isPlacementIdsValidForDefinition(currentPlacement, ks, y)
      ? { keyStageId: ks, yearId: y }
      : null;
  }, [
    allowExtendPlacement,
    currentPlacement,
    placementKeyStageName,
    yearValue,
    placementKeyStageId,
    placementYearId
  ]);

  const precedingOptions = useMemo(() => {
    const yId = placementFromE313?.yearId?.trim();
    const ksId = placementFromE313?.keyStageId?.trim();
    const catSid = catalogSubjectIdForSubmit?.trim();
    if (!tenantCustomCurriculumLibraryData || !catSid || !ksId || !yId) return [];
    return getCatalogTopicsForPlacement(tenantCustomCurriculumLibraryData, catSid, ksId, yId);
  }, [tenantCustomCurriculumLibraryData, catalogSubjectIdForSubmit, placementFromE313]);

  const existingYearLabelsForKeyStage = useMemo(() => {
    const ks = (currentPlacement?.keyStages ?? []).find(
      (k) => k.name.trim().toLowerCase() === placementKeyStageName.trim().toLowerCase()
    );
    return (ks?.years ?? [])
      .map((y) => y.yearName)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [currentPlacement, placementKeyStageName]);

  if (!showDialog) return null;

  const hasSubjects = hideSubjectPicker
    ? Boolean((initialCatalogSubjectId ?? selectedCatalogSubjectId).trim())
    : subjectRows.length > 0;

  const canSubmit =
    formReady &&
    catalogReady &&
    hasSubjects &&
    Boolean(selectedCatalogSubjectId.trim()) &&
    Boolean(resolvedEducationSystemKey) &&
    Boolean(catalogSubjectIdForSubmit?.trim()) &&
    placementReady &&
    (allowExtendPlacement
      ? placementKeyStageName.trim().length > 0
      : placementKeyStageId.trim().length > 0) &&
    yearValid &&
    topicName.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    let placementIds = placementFromE313;
    if (allowExtendPlacement && !placementIds && currentPlacement) {
      const systemId = tenantCustomSystemIdFromKey(resolvedEducationSystemKey);
      if (!systemId) {
        showAppMessage('Invalid custom education system key.', PLACEMENT_APP_MSG_TITLE.invalidSystem);
        setSubmitting(false);
        return;
      }
      const built = buildC314BandsEnsuringPlacement(
        currentPlacement,
        placementKeyStageName,
        placementYearLabel,
        () => crypto.randomUUID()
      );
      if (!built.ok) {
        showAppMessage(built.message, PLACEMENT_APP_MSG_TITLE.cannotSave);
        setSubmitting(false);
        return;
      }
      if (built.needsSave) {
        try {
          await sendSaveEducationStructure({
            C314_SystemId: systemId,
            C314_KeyStageBands: built.bands
          });
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : typeof e === 'string' ? e : 'Could not save education system.';
          showAppMessage(msg, PLACEMENT_APP_MSG_TITLE.couldNotSave);
          setSubmitting(false);
          return;
        }
        onLoadEducationSystemDefinitions?.();
      }
      placementIds = { keyStageId: built.keyStageId, yearId: built.yearId };
    }

    if (!placementIds && !allowExtendPlacement) {
      showAppMessage(
        'Could not resolve key stage and year. Refresh the page or check Control Panel → Education system.',
        PLACEMENT_APP_MSG_TITLE.notReady
      );
      setSubmitting(false);
      return;
    }

    if (!catalogSubjectIdForSubmit?.trim()) {
      showAppMessage(
        'Could not resolve catalog subject id for this subject and education system.',
        PLACEMENT_APP_MSG_TITLE.notReady
      );
      setSubmitting(false);
      return;
    }

    const cmd: Record<string, unknown> = {
      $type: 'C262v1_AddCustomTopicToTenantCustomCurriculum',
      C262_SubjectName: subjectName,
      C262_EducationSystemKey: resolvedEducationSystemKey,
      C262_CatalogSubjectId: catalogSubjectIdForSubmit.trim(),
      C262_EducationSystemKeyStageId: placementIds!.keyStageId,
      C262_EducationSystemYearId: placementIds!.yearId,
      C262_TopicName: topicName.trim(),
      C262_TopicDetails: topicDetails.trim() || undefined,
      C262_MinHours: minHours.trim() === '' ? undefined : Number(minHours),
      C262_PriorKnowledgeRequired:
        priorKnowledgeList.map((s) => s.trim()).filter(Boolean).join('|') || undefined,
      C262_NationalCurriculumContent:
        nationalCurriculumList.map((s) => s.trim()).filter(Boolean).join('|') || undefined,
      C262_Keywords:
        keywordRows
          .filter((k) => k.keyword.trim() !== '')
          .map((k) => `${k.keyword.trim()}:${k.description.trim() || ''}`)
          .join('|') || undefined,
      C262_LearningObjectives:
        learningObjectivesList.map((s) => s.trim()).filter(Boolean).join('|') || undefined,
      C262_SupervisionLevel: supervisionLevel.trim() || undefined,
      C262_Outcome: outcome.trim() || undefined,
      C262_TeacherTip: teacherTip.trim() || undefined,
      C262_Misconceptions: misconceptions.trim() || undefined,
      C262_PrecedingCatalogTopicId: precedingCatalogTopicId.trim() || undefined
    };

    onSubmit(cmd);
  };

  return (
    <>
      <AppMessageDialog
        open={appMessage != null}
        onOpenChange={(open) => {
          if (!open) setAppMessage(null);
        }}
        title={appMessage?.title ?? ''}
        message={appMessage?.message ?? ''}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="mx-4 flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900">Custom topic — {customCurriculumTitle}</h2>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {!catalogReady && <p className="text-sm text-gray-600">Loading catalog…</p>}

          {catalogReady && !hasSubjects && !hideSubjectPicker && (
            <div className="space-y-4">
              <div className="rounded-lg border border-action-blue/20 bg-action-blue/10 p-4 space-y-2">
                <p className="text-sm font-medium text-gray-900">
                  You do not have any custom subjects yet.
                </p>
                <p className="text-sm text-gray-700">
                  Add a custom subject first, then return here to create topics.
                </p>
              </div>
              <button
                type="button"
                onClick={() => onOpenCustomSubjectShell?.()}
                className="text-sm font-medium text-action-blue underline hover:text-action-blue-dark"
              >
                Add new custom subject
              </button>
            </div>
          )}

          {catalogReady && hasSubjects && !formReady && (
            <p className="text-sm text-gray-600">Loading form…</p>
          )}

          {formReady && catalogReady && hasSubjects && (
            <div className="space-y-4">
              {hideSubjectPicker ? (
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Subject</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {subjectName || 'Loading…'}
                  </p>
                </div>
              ) : (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Subject <span className="text-destructive-action">*</span>
                  </label>
                  <select
                    value={selectedCatalogSubjectId}
                    onChange={(e) => setSelectedCatalogSubjectId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                  >
                    <option value="">Select a subject</option>
                    {subjectRows.map((r) => (
                      <option key={r.catalogSubjectId} value={r.catalogSubjectId}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-600">
                    Only subjects you created appear here.{' '}
                    <button
                      type="button"
                      onClick={() => onOpenCustomSubjectShell?.()}
                      className="font-medium text-action-blue underline hover:text-action-blue-dark"
                    >
                      Add new custom subject
                    </button>
                  </p>
                </div>
              )}

              <div>
                {allowExtendPlacement ? (
                  <SelectOrCreateDropdown
                    label="Key stage"
                    value={placementKeyStageName}
                    onChange={handleKeyStagePlacementChange}
                    existingOptions={existingKeyStageNames}
                    allowCreate
                    placeholder={
                      !selectedCatalogSubjectId
                        ? 'Select a subject first'
                        : !resolvedEducationSystemKey
                          ? 'Loading placement…'
                          : placementReady
                            ? 'Select or add key stage'
                            : 'Loading…'
                    }
                    disabled={
                      !resolvedEducationSystemKey || !placementReady || placementStructureSaving || submitting
                    }
                    required
                  />
                ) : (
                  <>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Key stage <span className="text-destructive-action">*</span>
                    </label>
                    <select
                      value={placementKeyStageId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setPlacementKeyStageId(id);
                        const ks = (currentPlacement?.keyStages ?? []).find((k) => k.keyStageId === id);
                        if (ks) {
                          setPlacementKeyStageName(ks.name);
                          const y0 = ks.years[0];
                          setPlacementYearId(y0?.yearId ?? '');
                          setPlacementYearLabel(y0?.yearName ?? '');
                        }
                      }}
                      disabled={!resolvedEducationSystemKey || !placementReady || placementStructureSaving || submitting}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue disabled:bg-gray-100"
                    >
                      <option value="">
                        {!selectedCatalogSubjectId
                          ? 'Select a subject first'
                          : !resolvedEducationSystemKey
                            ? 'Loading placement…'
                            : placementReady
                              ? 'Select key stage'
                              : 'Loading…'}
                      </option>
                      {(currentPlacement?.keyStages ?? [])
                        .filter((ks) => ks.keyStageId)
                        .map((ks) => (
                          <option key={ks.keyStageId!} value={ks.keyStageId!}>
                            {ks.name}
                          </option>
                        ))}
                    </select>
                  </>
                )}
              </div>

              <div>
                {allowExtendPlacement ? (
                  <SelectOrCreateDropdown
                    label="Year"
                    value={placementYearLabel}
                    onChange={handleYearPlacementChange}
                    existingOptions={existingYearLabelsForKeyStage}
                    allowCreate={allowExtendPlacement}
                    placeholder={
                      !resolvedEducationSystemKey
                        ? 'Select a subject first'
                        : placementKeyStageName
                          ? 'Select or add year'
                          : 'Select key stage first'
                    }
                    disabled={
                      !placementKeyStageName ||
                      !resolvedEducationSystemKey ||
                      placementStructureSaving ||
                      submitting
                    }
                    required
                  />
                ) : (
                  <>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Year <span className="text-destructive-action">*</span>
                    </label>
                    <select
                      value={placementYearId}
                      onChange={(e) => {
                        const yid = e.target.value;
                        setPlacementYearId(yid);
                        const band = (currentPlacement?.keyStages ?? []).find(
                          (k) => k.keyStageId === placementKeyStageId
                        );
                        const y = band?.years.find((x) => x.yearId === yid);
                        if (y) setPlacementYearLabel(y.yearName);
                      }}
                      disabled={!placementKeyStageId || !resolvedEducationSystemKey}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue disabled:bg-gray-100"
                    >
                      <option value="">
                        {!resolvedEducationSystemKey
                          ? 'Select a subject first'
                          : placementKeyStageId
                            ? 'Select year'
                            : 'Select key stage first'}
                      </option>
                      {getYearsForKeyStageId(currentPlacement, placementKeyStageId).map((y) => (
                        <option key={y.yearId} value={y.yearId}>
                          {y.yearName}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Topic name <span className="text-destructive-action">*</span>
                </label>
                <input
                  type="text"
                  value={topicName}
                  onChange={(e) => setTopicName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Topic details</label>
                <textarea
                  value={topicDetails}
                  onChange={(e) => setTopicDetails(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Minimum hours</label>
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={minHours}
                  onChange={(e) => setMinHours(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prior knowledge required (optional)</label>
                <div className="space-y-2">
                  {priorKnowledgeList.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const next = [...priorKnowledgeList];
                          next[index] = e.target.value;
                          setPriorKnowledgeList(next);
                        }}
                        placeholder="Prior knowledge item"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                      />
                      <button
                        type="button"
                        onClick={() => setPriorKnowledgeList(priorKnowledgeList.filter((_, i) => i !== index))}
                        className="px-3 py-2 text-sm text-destructive-action hover:text-destructive-action-dark"
                        aria-label="Remove item"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPriorKnowledgeList([...priorKnowledgeList, ''])}
                    className="flex items-center gap-1 text-sm text-action-blue hover:text-action-blue-dark"
                  >
                    <Plus size={16} />
                    Add prior knowledge item
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  National curriculum content (optional)
                </label>
                <div className="space-y-2">
                  {nationalCurriculumList.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const next = [...nationalCurriculumList];
                          next[index] = e.target.value;
                          setNationalCurriculumList(next);
                        }}
                        placeholder="Curriculum content item"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                      />
                      <button
                        type="button"
                        onClick={() => setNationalCurriculumList(nationalCurriculumList.filter((_, i) => i !== index))}
                        className="px-3 py-2 text-sm text-destructive-action hover:text-destructive-action-dark"
                        aria-label="Remove item"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNationalCurriculumList([...nationalCurriculumList, ''])}
                    className="flex items-center gap-1 text-sm text-action-blue hover:text-action-blue-dark"
                  >
                    <Plus size={16} />
                    Add curriculum content item
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Keywords (optional)</label>
                <div className="space-y-2">
                  {keywordRows.map((row, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={row.keyword}
                        onChange={(e) => {
                          const next = [...keywordRows];
                          next[index] = { ...next[index], keyword: e.target.value };
                          setKeywordRows(next);
                        }}
                        placeholder="Keyword"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                      />
                      <input
                        type="text"
                        value={row.description}
                        onChange={(e) => {
                          const next = [...keywordRows];
                          next[index] = { ...next[index], description: e.target.value };
                          setKeywordRows(next);
                        }}
                        placeholder="Description (optional)"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                      />
                      <button
                        type="button"
                        onClick={() => setKeywordRows(keywordRows.filter((_, i) => i !== index))}
                        className="px-3 py-2 text-sm text-destructive-action hover:text-destructive-action-dark"
                        aria-label="Remove keyword"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setKeywordRows([...keywordRows, { keyword: '', description: '' }])}
                    className="flex items-center gap-1 text-sm text-action-blue hover:text-action-blue-dark"
                  >
                    <Plus size={16} />
                    Add keyword
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Learning objectives (optional)</label>
                <div className="space-y-2">
                  {learningObjectivesList.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const next = [...learningObjectivesList];
                          next[index] = e.target.value;
                          setLearningObjectivesList(next);
                        }}
                        placeholder="Learning objective"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setLearningObjectivesList(learningObjectivesList.filter((_, i) => i !== index))
                        }
                        className="px-3 py-2 text-sm text-destructive-action hover:text-destructive-action-dark"
                        aria-label="Remove objective"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setLearningObjectivesList([...learningObjectivesList, ''])}
                    className="flex items-center gap-1 text-sm text-action-blue hover:text-action-blue-dark"
                  >
                    <Plus size={16} />
                    Add learning objective
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Supervision level</label>
                <input
                  type="text"
                  value={supervisionLevel}
                  onChange={(e) => setSupervisionLevel(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Outcome</label>
                <textarea
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Teacher tip</label>
                <textarea
                  value={teacherTip}
                  onChange={(e) => setTeacherTip(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Misconceptions</label>
                <textarea
                  value={misconceptions}
                  onChange={(e) => setMisconceptions(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Preceding topic (order)</label>
                <select
                  value={precedingCatalogTopicId}
                  onChange={(e) => setPrecedingCatalogTopicId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-action-blue focus:outline-none focus:ring-2 focus:ring-action-blue"
                >
                  <option value="">— None (add at start of year) —</option>
                  {precedingOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 justify-end gap-3 border-t border-gray-200 p-6">
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-lg bg-action-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-action-blue-dark disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {submitting ? 'Saving…' : 'Add topic'}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
